import { useEffect, useMemo, useState } from "react";
import { useApiClient } from "@Front/Hooks/useApiClient";
import { useLatestRef } from "@Front/Hooks/useLatestRef";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { userFacingMessageFromApiClientError } from "@Front/Utils/userFacingMessageFromApiClientError";
import {
	buildStripeSubscriptionSnapshotPath,
	type StripeBillingSubscriptionSnapshotResponse,
} from "@Front/Api/LeCoffreApi/Admin/Stripe/stripeFrontContracts";

type StripeSubscriptionSnapshotState = {
	loading: boolean;
	errorMessage: string | null;
	quantity: number | null;
	status: string | null;
	cancelAtUnix: number | null;
};

type StripeSnapshotLoadOptions = {
	stripeSubscriptionId: string | null | undefined;
	enabled: boolean;
	logLabel: string;
	errorFallbackMessage: string;
	refreshTrigger?: number;
};

export type StripeSubscriptionSnapshot = StripeSubscriptionSnapshotState & {
	isLinkedToStripe: boolean;
};

type LoadStripeSubscriptionSnapshotParams = {
	get: ReturnType<typeof useApiClient>["get"];
	stripeSubscriptionId: string;
	isCancelled: () => boolean;
	setErrorMessage: (value: string | null) => void;
	setQuantity: (value: number | null) => void;
	setStatus: (value: string | null) => void;
	setCancelAtUnix: (value: number | null) => void;
	errorFallbackMessage: string;
	logLabel: string;
};

function resolveStripeBillableQuantity(payload: StripeBillingSubscriptionSnapshotResponse | null | undefined): number | null {
	const firstItem = payload?.items?.data?.[0];
	return typeof firstItem?.quantity === "number" ? firstItem.quantity : null;
}

export function isStripeSubscriptionId(value: string | null | undefined): value is string {
	return typeof value === "string" && value.startsWith("sub_");
}

function resetStripeSnapshotState(params: {
	setLoading: (value: boolean) => void;
	setErrorMessage: (value: string | null) => void;
	setQuantity: (value: number | null) => void;
	setStatus: (value: string | null) => void;
	setCancelAtUnix: (value: number | null) => void;
}): void {
	params.setLoading(false);
	params.setErrorMessage(null);
	params.setQuantity(null);
	params.setStatus(null);
	params.setCancelAtUnix(null);
}

async function loadStripeSubscriptionSnapshot(params: LoadStripeSubscriptionSnapshotParams): Promise<void> {
	const {
		get,
		stripeSubscriptionId,
		isCancelled,
		setErrorMessage,
		setQuantity,
		setStatus,
		setCancelAtUnix,
		errorFallbackMessage,
		logLabel,
	} = params;
	try {
		const response = await get<StripeBillingSubscriptionSnapshotResponse>(buildStripeSubscriptionSnapshotPath(stripeSubscriptionId), {
			logErrors: false,
			background: true,
		});
		if (isCancelled()) return;
		const payload = response.data ?? null;
		setStatus(typeof payload?.status === "string" ? payload.status : null);
		setQuantity(resolveStripeBillableQuantity(payload));
		setCancelAtUnix(typeof payload?.cancel_at === "number" ? payload.cancel_at : null);
	} catch (error: unknown) {
		LoggerService.error(`❌ [${logLabel}] stripe snapshot load failed`, { error });
		if (isCancelled()) return;
		setErrorMessage(userFacingMessageFromApiClientError(error, errorFallbackMessage));
		setStatus(null);
		setQuantity(null);
		setCancelAtUnix(null);
	}
}

function useStripeSubscriptionSnapshotState(
	options: StripeSnapshotLoadOptions,
): StripeSubscriptionSnapshotState {
	const { stripeSubscriptionId, enabled, logLabel, errorFallbackMessage, refreshTrigger = 0 } = options;
	const { get } = useApiClient();
	const getRef = useLatestRef(get);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [quantity, setQuantity] = useState<number | null>(null);
	const [status, setStatus] = useState<string | null>(null);
	const [cancelAtUnix, setCancelAtUnix] = useState<number | null>(null);

	useEffect(() => {
		if (!enabled || !isStripeSubscriptionId(stripeSubscriptionId)) {
			resetStripeSnapshotState({ setLoading, setErrorMessage, setQuantity, setStatus, setCancelAtUnix });
			return;
		}
		let cancelled = false;
		setLoading(true);
		setErrorMessage(null);
		void loadStripeSubscriptionSnapshot({
			get: getRef.current,
			stripeSubscriptionId,
			isCancelled: () => cancelled,
			setErrorMessage,
			setQuantity,
			setStatus,
			setCancelAtUnix,
			errorFallbackMessage,
			logLabel,
		}).finally(() => {
			if (!cancelled) {
				setLoading(false);
			}
		});
		return (): void => {
			cancelled = true;
		};
	}, [enabled, errorFallbackMessage, logLabel, refreshTrigger, stripeSubscriptionId, getRef]);

	return { loading, errorMessage, quantity, status, cancelAtUnix };
}

export function useStripeSubscriptionSnapshot(options: StripeSnapshotLoadOptions): StripeSubscriptionSnapshot {
	const { stripeSubscriptionId } = options;
	const state = useStripeSubscriptionSnapshotState(options);
	return useMemo(
		() => ({
			loading: state.loading,
			errorMessage: state.errorMessage,
			quantity: state.quantity,
			status: state.status,
			cancelAtUnix: state.cancelAtUnix,
			isLinkedToStripe: isStripeSubscriptionId(stripeSubscriptionId),
		}),
		[state, stripeSubscriptionId],
	);
}
